type ObserverCallback = (entry: IntersectionObserverEntry) => void;

interface SharedObserverBucket {
  observer: IntersectionObserver;
  callbacksByTarget: Map<Element, Set<ObserverCallback>>;
}

interface ObserverOptions {
  threshold: number | number[];
  rootMargin: string;
}

const observerPool = new Map<string, SharedObserverBucket>();

function thresholdKey(threshold: number | number[]): string {
  if (Array.isArray(threshold)) {
    return threshold.join(',');
  }
  return String(threshold);
}

function buildObserverKey(options: ObserverOptions): string {
  return `${thresholdKey(options.threshold)}|${options.rootMargin}`;
}

function canUseIntersectionObserver(): boolean {
  return typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined';
}

function getSharedObserver(options: ObserverOptions): SharedObserverBucket {
  const key = buildObserverKey(options);
  const existing = observerPool.get(key);
  if (existing) return existing;

  const callbacksByTarget = new Map<Element, Set<ObserverCallback>>();
  const bucket: SharedObserverBucket = {
    observer: new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const callbacks = callbacksByTarget.get(entry.target);
          if (!callbacks) continue;
          for (const callback of callbacks) {
            callback(entry);
          }
        }
      },
      {
        threshold: options.threshold,
        rootMargin: options.rootMargin,
      }
    ),
    callbacksByTarget,
  };

  observerPool.set(key, bucket);
  return bucket;
}

export function observeWithSharedObserver(
  target: Element,
  options: ObserverOptions,
  callback: ObserverCallback
): () => void {
  if (!canUseIntersectionObserver()) {
    return () => {
      // noop
    };
  }

  const key = buildObserverKey(options);
  const bucket = getSharedObserver(options);
  const existingCallbacks = bucket.callbacksByTarget.get(target);

  if (existingCallbacks) {
    existingCallbacks.add(callback);
  } else {
    bucket.callbacksByTarget.set(target, new Set([callback]));
    bucket.observer.observe(target);
  }

  return () => {
    const callbacks = bucket.callbacksByTarget.get(target);
    if (!callbacks) return;

    callbacks.delete(callback);
    if (callbacks.size > 0) return;

    bucket.callbacksByTarget.delete(target);
    bucket.observer.unobserve(target);

    if (bucket.callbacksByTarget.size === 0) {
      bucket.observer.disconnect();
      observerPool.delete(key);
    }
  };
}
